/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useMemo} from 'react';
import {useSelector} from 'react-redux';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {CRUDPermissions} from '@ion/core/src/types/entities';
import {VALID_MANUAL_DATE_FORMATS} from '@ion/core/src/utils/DateUtil';
import {BackdropComponent} from '@ion/dls/src/components/backdrop/Backdrop';
import {UserEditor} from '@ion/dls/src/editors/UserEditor';
import Render from '@ion/dls/src/render/Render';
import {useCurrentUser} from '@ion/query/src/hooks/useCurrentUser';
import {getRouteLabel} from '@/routes/RouteDefinitions';
import Features, {
  ADMIN_USERS_PAGE,
  hasAppGroupChooserAccess,
  hasAppNotificationAccess,
  TRACKING_PAGE2
} from '@/security/Features';
import {ApplicationKeys} from '@/types/application';
import {
  editUserSelector,
  userRolesSelector
} from '../../../store/selectors/admin-users-selectors';
import {globalGroupAccountsMapSelector} from '../../../store/selectors/global-group-chooser-selectors';
import {
  initialAccountsSelector
} from '../../../store/selectors/initialization-selectors';
import {
  userAccountSelector,
  userSelector
} from '../../../store/selectors/login-selectors';
import {
  pagesSelector
} from '../../../store/selectors/nav-selectors';
import useUser from './useUser';

/**
 * @EditUserEditor
 *
 * The EditUserEditor component provides the editor which is used for editing the current
 * user (i.e. the logged in user) as well as for allowing the current user to edit other
 * users. This component must be accessible throughout the application and not just from
 * the Admin Users Page; thus, it is only to be added once, and should only be added in
 * the LayoutComponent.
 */
export const EditUserEditor = () => {
  const canAccessGroupChooser = hasAppGroupChooserAccess();
  const canAccessNotification = hasAppNotificationAccess();
  const roles                 = useSelector(userRolesSelector);
  const currentUser           = useSelector(userSelector);
  const account               = useSelector(userAccountSelector);
  const pages                 = useSelector(pagesSelector);
  const subAccounts           = useSelector(initialAccountsSelector);
  const accountsMap           = useSelector(globalGroupAccountsMapSelector);

  const canManageUsers: boolean          = Features.hasFeature(ADMIN_USERS_PAGE);
  const usersPermissions: CRUDPermissions = {
    hasAddPermission    : canManageUsers,
    hasUpdatePermission : canManageUsers
  };

  const {
    selected: selectedUser,
    roleFilterAccountIds,
    isCurrentUser,
    globalGroup,
    loading,
    onSave,
    onCancel
  } = useUser(editUserSelector, true);

  // the GraphQL currentUser query used to populate `currentUser` above does
  // not return `usertype`, so the real value is fetched via this REST
  // endpoint only when the current user opens their own profile - the form's
  // initial state is captured once at mount, so this must resolve before
  // UserEditor mounts, not merely before it renders ...
  const {currentUser: restCurrentUser, loading: userTypeLoading} = useCurrentUser(isCurrentUser);
  const isUserDataReady = !isCurrentUser || !userTypeLoading;

  const profileProps = useMemo(() => {
    if (isCurrentUser) {
      return ({
        applicationPages            : pages,
        defaultPageId               : TRACKING_PAGE2,
        preferencesAPI              : UserPreferences,
        pageNameResolver            : getRouteLabel,
        showDefaultGroup            : canAccessGroupChooser,
        showNotificationPreferences : canAccessNotification,
        accountsMap
      });
    }
  }, [accountsMap, canAccessGroupChooser, canAccessNotification, isCurrentUser, pages]);

  return (
    <Render>
      <Render.If condition={isUserDataReady}>
        <UserEditor open
          allowManualDateEntry
          includeDateFormat
          includeTimeFormat
          isViewDialog={false}
          roles={roles}
          onSave={onSave}
          loading={loading}
          onCancel={onCancel}
          account={globalGroup}
          subAccounts={subAccounts}
          userAccount={account}
          usersPermissions={usersPermissions}
          profileProps={profileProps}
          isCurrentUser={isCurrentUser}
          currentUserId={currentUser?.id}
          user={!isCurrentUser ? selectedUser : {
            ...currentUser,
            usertype : restCurrentUser?.usertype,
            operator : selectedUser?.operator,
            version  : selectedUser?.version
          }}
          namespace={ApplicationKeys.iOnSuite}
          validDateFormats={VALID_MANUAL_DATE_FORMATS}
          roleFilterAccountIds={roleFilterAccountIds} />
      </Render.If>
      <Render.Else>
        <BackdropComponent open />
      </Render.Else>
    </Render>
  );
};

export default memo(EditUserEditor);
